import type React from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';

//Redux
import {
  initIntentConversation,
  setFreeFormQuery,
} from 'actions/workflows-v2/workflowsV2';
import {
  selectSelectedIntentIndex,
  selectWorkflowsConfig,
  selectFreeFormQuery,
  selectLoading,
  selectError,
  selectFeatureFlags,
  selectInitialIntentId,
  selectIsAuthorIconVisible,
  selectWidgetComponents,
  selectConversationStatus,
} from 'reducers/workflows';

//Selectors
import { selectIsMobile } from 'reducers/main';
import {
  selectCustomConfigParamsGreetingLine,
  selectGreetingLine,
  selectIsInteractiveEmail,
  selectPreviewIsLiveMode,
} from 'reducers/config';
import { setSelectedIntentIndex } from 'actions/workflows/workflows';

//Components
import IntentFormWrapper from '../FormWrapper/IntentFormWrapper';
import OptionButton from '../OptionButton';
import AgathaMessage from '../AgathaMessage/AgathaMessage';

//Types
import type { InitIntentInterface, SelectIntentComponent } from 'api/types';
import { Box } from '@mui/material';

interface SelectIntentStepProps {
  areLoadingDotsVisible: boolean;
  isLastStep: boolean;
  setStepSent: (query?: string) => void;
  stepIndex: number;
  widgetComponent: SelectIntentComponent;
}

const SelectIntentStep: React.FC<
  React.PropsWithChildren<SelectIntentStepProps>
> = ({
  areLoadingDotsVisible,
  isLastStep,
  setStepSent,
  stepIndex,
  widgetComponent,
}) => {
  const dispatch = useAppDispatch();
  //state
  const query = useAppSelector(selectFreeFormQuery);

  //selectors
  const featureFlags = useAppSelector(selectFeatureFlags);
  const isMobile = useAppSelector(selectIsMobile);
  const workflowConfig = useAppSelector(selectWorkflowsConfig);
  const selectedIntentIndex = useAppSelector(selectSelectedIntentIndex);
  const loading = useAppSelector(selectLoading);
  const error = useAppSelector(selectError);
  const greetingLine = useAppSelector(selectGreetingLine);
  const customConfigParamsGreetingLine = useAppSelector(
    selectCustomConfigParamsGreetingLine,
  );
  const previewIsLiveMode = useAppSelector(selectPreviewIsLiveMode);
  const initialIntentId = useAppSelector(selectInitialIntentId);
  const conversationStatus = useAppSelector(selectConversationStatus);

  const isAlwaysUseInitialIntentIdEnabled = featureFlags.includes(
    'always_use_initial_intent_id',
  );
  const isCenterAlignButtonOptionsEnabled = featureFlags.includes(
    'center_align_button_options',
  );
  const isInteractiveEmail = useAppSelector(selectIsInteractiveEmail);
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );
  const widgetComponents = useAppSelector(selectWidgetComponents);
  if (
    ((isAlwaysUseInitialIntentIdEnabled || isInteractiveEmail) &&
      !!initialIntentId) ||
    // Hide the step if we have `initialIntentId` and it's the beginning of a
    // conversation:
    (initialIntentId &&
      widgetComponents.length === 1 &&
      conversationStatus === 'initial')
  ) {
    return null;
  }

  const showTopIntents = widgetComponent.component_fields.show_top_n_intents;
  const showGreetingMessage =
    widgetComponent.component_fields.show_greeting_message;
  const customGreeting = showGreetingMessage
    ? widgetComponent.component_fields.custom_greeting
    : '';

  const intents = showTopIntents ? workflowConfig?.top_n_intents : [];
  let greeting = showGreetingMessage ? greetingLine : '';
  const enableInput =
    (workflowConfig?.free_form_intent_detection_enabled &&
      !error &&
      !areLoadingDotsVisible) ||
    false;
  const isQueryEmpty = query.trim() === '';
  const isSubmitDisabled = isQueryEmpty || loading;
  const isSent = widgetComponent.isSent;
  const isReroutedByNegativeFeedback =
    !!widgetComponent.isReroutedByNegativeFeedback;
  const translatedGreeting = showGreetingMessage
    ? widgetComponent.component_fields.translated_greeting_line
    : '';

  if (customGreeting) greeting = customGreeting;

  function handleSubmit(latestState: {
    query: typeof query;
    selectedIntentIndex: typeof selectedIntentIndex;
  }) {
    let selectedIntent: InitIntentInterface = {};
    if (
      (enableInput && latestState.selectedIntentIndex === intents?.length) ||
      latestState.query.trim()
    ) {
      selectedIntent = { free_form_query: latestState.query };
      setStepSent(selectedIntent.free_form_query);
      dispatch(setFreeFormQuery(''));
    } else if (latestState.selectedIntentIndex !== null && intents) {
      const latestSelectedIntent = intents[latestState.selectedIntentIndex];
      if (latestSelectedIntent) {
        selectedIntent = {
          intent_id: latestSelectedIntent.intent_id,
        };
        setStepSent(
          intents.find(
            intent => intent.intent_id === latestSelectedIntent.intent_id,
          )?.intent_name || '',
        );
      }
    }

    dispatch(initIntentConversation({ intent: selectedIntent }));
  }

  if (isSent || isReroutedByNegativeFeedback) {
    return (
      <>
        {(greeting || translatedGreeting) && (
          <AgathaMessage
            isAuthorIconVisible={isAuthorIconVisible}
            stepIndex={stepIndex}
          >
            {customConfigParamsGreetingLine ?? translatedGreeting ?? greeting}
          </AgathaMessage>
        )}
      </>
    );
  } else
    return (
      <IntentFormWrapper
        formTitle={
          customConfigParamsGreetingLine ?? translatedGreeting ?? greeting
        }
        isDisabled={isSubmitDisabled}
        isForm={false}
        isLastStep={isLastStep}
        stepIndex={stepIndex}
      >
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: isCenterAlignButtonOptionsEnabled
              ? 'center'
              : 'flex-end',
            paddingBottom: '16px',
            paddingLeft: isCenterAlignButtonOptionsEnabled ? '0' : '24px',
          }}
        >
          {previewIsLiveMode !== false &&
            intents?.map((intent, index: number) => (
              <OptionButton
                aria-label={intent.intent_name}
                isDisabled={Boolean(error)}
                isMobile={isMobile}
                isSelected={selectedIntentIndex === index}
                isSelectIntentStep
                key={`${intent.intent_name}-${index}`}
                onClick={() => {
                  dispatch(setSelectedIntentIndex(index));
                  handleSubmit({
                    query: '',
                    selectedIntentIndex: index,
                  });
                }}
                text={intent.intent_name}
                url={intent.href ?? undefined}
              />
            ))}
        </Box>
      </IntentFormWrapper>
    );
};

export default SelectIntentStep;
